// React core
import { useEffect, useState } from "react";

// MUI components & icons
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Typography,
} from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";

// Styles
import mobile_user_portal_styles, {
  accordion_styles,
} from "../mobile_user_portal_styles";
import alert_styles from "../../../Components/UserPortalPage/Body/Memberships/Alert/alert_styles";
import card_styles from "../../../Components/UserPortalPage/Body/Memberships/Card/card_styles";

// Utilities and services
import {
  adjustDateIfInTerm,
  format_date,
  format_price,
  getFormattedCancelDate,
} from "../../../services/global.services";
import { cancel_agreement } from "../../../services/api.calls.services";

// State management hooks and actions
import { useAppDispatch, useAppSelector } from "../../../services/StoreHelper";
import { update_state_value } from "../../../store/reducers/AppReducer";

// Local components
import CancelConfirm from "../../../Components/UserPortalPage/Body/Memberships/CancelConfirm/CancelConfirm";
import ProcessingDialog from "../../../Components/UserPortalPage/Body/Memberships/ProcessingDialog/ProcessingDialog";
import ErrorDialog from "../../../Components/UserPortalPage/Body/Memberships/ErrorDialog/ErrorDialog";

function MobileCard({ contract, refresh }: any) {
  const dispatch = useAppDispatch();
  const { user, temp_token, club } = useAppSelector((state) => state.app);

  // 🔹 simple toggle
  const { usesSpanish } = club;

  const [expanded, setExpanded] = useState(true);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessingCancelConfirm, setShowProcessingCancelConfirm] =
    useState(false);
  const [showError, setShowError] = useState(false);
  const [note, setNote] = useState("");

  const inTerm = contract.installmentsRemaining ? "in-term" : "out-of-term";

  useEffect(() => {}, []);

  const toggleCancelConfirm = (open = false) => setShowCancelConfirm(open);

  const toggleProcessingCancelConfirm = async (open = false) => {
    dispatch(update_state_value({ key: "cancel_process", value: true }));

    const isInTerm = !!contract.installmentsRemaining;
    if (isInTerm) contract.agreement_processing = true;

    setShowCancelConfirm(false);

    const userToken = JSON.parse(localStorage.getItem("user_token") || "null");
    const authToken = userToken
      ? userToken.expires_in
        ? userToken.access_token
        : userToken.refresh_token
      : temp_token;

    const noticePeriodDays =
      !isInTerm && contract.noticePeriodDays ? contract.noticePeriodDays : 0;

    const cancelDate: any = getFormattedCancelDate(
      contract,
      true,
      noticePeriodDays
    );

    cancel_agreement(
      authToken,
      contract.agreementId,
      cancelDate,
      note,
      isInTerm,
      user.UserID
    )
      .then(() => {
        setShowProcessingCancelConfirm(open);
        let url = club.onlineCancelUrl;
        if (url) {
          if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
          window.location.replace(url);
        }
      })
      .catch(() => setShowError(open));

    dispatch(update_state_value({ key: "cancel_process", value: false }));
  };

  const get_status_text = () => {
    if (contract.cancelDateUtc)
      return usesSpanish
        ? `Cancelado el ${format_date(contract.cancelDateUtc)}`
        : `Cancelled On ${format_date(contract.cancelDateUtc)}`;

    if (contract.frozen)
      return usesSpanish ? "Activo - Congelado" : "Active - Frozen";

    if (contract.cancellationPendingReview)
      return usesSpanish
        ? "Activo - Cancelación solicitada"
        : "Active - Cancel Requested";

    if (contract.scheduledForCancellingOnUtc)
      return usesSpanish ? "Activo - Por cancelar" : "Active - To Be Cancelled";

    if (!contract.installmentsRemaining && !contract.isAutoRenewed)
      return usesSpanish ? "Activo - Pagado por completo" : "Active - Fully Paid";

    return usesSpanish
      ? `Activo - Plazo ${inTerm === "in-term" ? "No " : ""}cumplido`
      : `Active - Term ${inTerm === "in-term" ? "Not " : ""}Fulfilled`;
  };

  const renderCancelButton = () => {
    if (
      (contract.agreement_processing ||
        contract.scheduledForCancellingOnUtc ||
        contract.cancelDateUtc ||
        contract.cancellationPendingReview) &&
      !contract.frozen
    )
      return null;

    const isDisabled =
      !contract.canCancelOnline ||
      (!contract.installmentsRemaining && !contract.isAutoRenewed) ||
      contract.frozen ||
      contract.excludeOnlineCancelIfPurchasedInPerson;

    return (
      <Button
        style={
          isDisabled
            ? mobile_user_portal_styles.cancel_button_disabled
            : mobile_user_portal_styles.cancel_button
        }
        onClick={() => toggleCancelConfirm(true)}
        disabled={isDisabled}
      >
        {usesSpanish ? "CANCELAR" : "CANCEL"}
      </Button>
    );
  };

  return (
    <Accordion
      style={mobile_user_portal_styles.card}
      sx={accordion_styles(expanded)}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary style={mobile_user_portal_styles.top}>
        <div style={mobile_user_portal_styles.status_container}>
          <div style={mobile_user_portal_styles.status_tile_active}>
            <h6 style={mobile_user_portal_styles.status_headline}>
              {get_status_text()}
            </h6>
          </div>
        </div>

        <div style={mobile_user_portal_styles.top_text}>
          <h4 style={mobile_user_portal_styles.membership_name}>
            {contract.name}
          </h4>
        </div>

        {contract.nextPaymentDueDateUtc && contract.nextPaymentAmount && (
          <div style={mobile_user_portal_styles.next_payment_on_date_container}>
            <p style={mobile_user_portal_styles.npod_label}>
              {usesSpanish ? "Próximo pago:" : "Next Payment:"}
            </p>
            <p style={mobile_user_portal_styles.npod_text}>
              {format_price(
                (contract.nextPaymentAmount ?? 0) +
                  (contract.nextPaymentTaxAmount ?? 0)
              )}
            </p>
            <p style={mobile_user_portal_styles.npod_label}>
              {usesSpanish ? "el" : "on"}
            </p>
            <h6 style={mobile_user_portal_styles.npod_text}>
              {format_date(contract.nextPaymentDueDateUtc)}
            </h6>
          </div>
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ padding: 0 }}>
        <div style={mobile_user_portal_styles.cancellation_container}>
          {renderCancelButton()}
        </div>

        <Alert severity="warning" sx={mobile_user_portal_styles.alert}>
          <Typography sx={alert_styles.alert_text_custom}>
            {usesSpanish
              ? "Este acuerdo tiene facturas vencidas."
              : "This agreement currently has past due invoices."}
          </Typography>
        </Alert>

        <CancelConfirm
          contract={contract}
          open={showCancelConfirm}
          onClose={() => toggleCancelConfirm(false)}
          onCancel={() => toggleProcessingCancelConfirm(true)}
          setNote={setNote}
          cancelDate={getFormattedCancelDate(
            contract,
            false,
            contract.noticePeriodDays
          )}
        />

        <ProcessingDialog
          message={
            usesSpanish
              ? "Tu acuerdo se ha cancelado correctamente."
              : "Your agreement has been successfully cancelled."
          }
          open={showProcessingCancelConfirm}
          onClose={() => setShowProcessingCancelConfirm(false)}
          inTerm={inTerm}
          onlineCancelReview={contract.onlineCancelReview}
        />

        <ErrorDialog
          error={
            usesSpanish
              ? "Ocurrió un error. Comunícate con el club."
              : "An unknown error has occurred please contact your club."
          }
          open={showError}
          onClose={() => setShowError(false)}
          contract={contract}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default MobileCard;